import React, { useEffect, useState } from 'react';
import type { Tooltip as TooltipType } from '@reach/tooltip';
import { Result } from 'models/Search';
import Spinner from 'shared/components/Spinner';
import InlineAlert from 'shared/edit/InlineAlert';
import XCircleSVG from '../../svg/XCircleSVG';
import DownChevronSVG from 'shared/components/svg/DownChevronSVG';
import UpChevronSVG from 'shared/components/svg/UpChevronSVG';
import useWindowSize from 'shared/hooks/useWindowSize';

const Tooltip = React.lazy<typeof TooltipType>(() => import('@reach/tooltip'));

interface CompareTilesProps {
	/**
	 * Image url for compare item.
	 *
	 * @type {string}
	 * @memberof CompareTilesProps
	 */
	imageURL: string;

	/**
	 * Name of the item.
	 *
	 * @type {string}
	 * @memberof CompareTilesProps
	 */
	itemName: string;

	/**
	 * The item data itself.
	 *
	 * @type {Result}
	 * @memberof CompareTilesProps
	 */
	item?: Result;

	/**
	 * What happens when a tile is selected.
	 *
	 * @memberof CompareTilesProps
	 */
	onSelectTiles: (item: Result) => void;
}

interface CompareItemProps {
	/**
	 * The list of items themselves.
	 *
	 * @type {Result[]}
	 * @memberof CompareItemProps
	 */
	itemsList: Result[];

	/**
	 * Logic for when a compare button is pressed.
	 *
	 * @memberof CompareItemProps
	 */
	onSelectCompareItems: () => void;

	/**
	 * Clearing logic.
	 *
	 * @memberof CompareItemProps
	 */
	clearItems: () => void;

	/**
	 * What happens when you click a tile.
	 *
	 * @memberof CompareItemProps
	 */
	onSelectTiles: (item: Result) => void;
	error: boolean;
}

/**
 * The tiles for each compare item w/ the tooltip from reach UI to display the name.
 *
 * @param {CompareTilesProps} { imageURL, itemName, onSelectTiles, item }
 * @returns
 */
function CompareTiles({ imageURL, itemName, onSelectTiles, item }: CompareTilesProps) {
	if (item && onSelectTiles) {
		return (
			<React.Suspense fallback={<Spinner />}>
				<Tooltip label={itemName} as={'div'}>
					<button
						type="button"
						onClick={() => onSelectTiles(item)}
						className="hawk-compare-bar__list-item hawk-compare-bar__list-item--has-image"
					>
						<div className="hawk-compare-bar__list-item-x">
							<XCircleSVG class="hawk-compare-bar__list-item-x-icon" />
						</div>
						{imageURL ? (
							<img className="hawk-compare-bar__list-item-thumb" src={imageURL} alt={itemName} />
						) : (
							<div className="hawk-compare-bar__list-item-placeholder" />
						)}
					</button>
				</Tooltip>
			</React.Suspense>
		);
	}

	return <div className="hawk-compare-bar__list-item">{imageURL && <img src={imageURL} alt={itemName} />}</div>;
}

/**
 * Component that handles the actual map of the compare items.
 *
 * @param {CompareItemProps} { itemsList, onSelectCompareItems, clearItems, onSelectTiles, error }
 * @returns
 */
function CompareItems({ itemsList, onSelectCompareItems, clearItems, onSelectTiles, error }: CompareItemProps) {
	const [compareHidden, setCompareHidden] = useState(false);
	const size = useWindowSize();

	function onMobileToggleCompare() {
		setCompareHidden(!compareHidden);
	}

	useEffect(() => {
		setCompareHidden(false);
	}, [itemsList]);

	const isMobile = size && size.width && size.width < 768;
	const isCollapsed = isMobile && compareHidden;

	return (
		<>
			<div className="hawk-compare-bar__inner">
				{!isMobile ? (
					<div className="hawk-compare-bar__inner-item hawk-compare-bar__inner-item--label">
						<span className="hawk-compare-bar__label">
							<span className="hawk-compare-bar__sub-label">Compare up to 5 items</span>
						</span>
					</div>
				) : null}

				{!isCollapsed ? (
					<div className="hawk-compare-bar__inner-item hawk-compare-bar__inner-item--list">
						<div className="hawk-compare-bar__body">
							<div className="hawk-compare-bar__list">
								{[...Array(5)].map((_, index) => {
									if (itemsList && itemsList.length && itemsList[index]) {
										const imageURL = itemsList[index].getDocumentValue('image');
										const itemName = itemsList[index].getDocumentValue('name');
										return (
											<CompareTiles
												onSelectTiles={onSelectTiles}
												imageURL={imageURL || ''}
												itemName={itemName || ''}
												item={itemsList[index]}
												key={index}
											/>
										);
									}

									return (
										<CompareTiles
											onSelectTiles={onSelectTiles}
											imageURL={''}
											itemName={''}
											key={index}
										/>
									);
								})}
							</div>
						</div>
					</div>
				) : null}
				<div className="hawk-compare-bar__inner-item hawk-compare-bar__inner-item--actions">
					<div className="hawk-compare-bar__actions">
						<button
							disabled={itemsList.length < 2}
							onClick={() => onSelectCompareItems()}
							className="hawk-btn hawk-btn-primary hawk-compare-bar__action"
						>
							Compare
						</button>
						<button
							onClick={() => clearItems()}
							className="hawk-btn hawk-btn-primary-outline hawk-compare-bar__action"
						>
							Clear
						</button>
						<button
							aria-expanded
							onClick={onMobileToggleCompare}
							type="button"
							className="hawk-btn hawk-btn-primary-outline hawk-compare-bar__action hawk-compare-bar__action--toggle"
						>
							{isCollapsed ? <UpChevronSVG /> : <DownChevronSVG />}
							<span className="roc-visually-hidden">Collapse the compare bar</span>
						</button>
					</div>
				</div>
			</div>

			{error ? <InlineAlert message="Something went wrong. Please try again." /> : null}
		</>
	);
}

export default CompareItems;
